import { Fragment, useEffect, useRef, useState } from 'react';
import { scApi, type MatchDetail, type SGroup } from '../sportcenter';
import { hubApi, useHideScores } from '../sporthub';
import { Crest } from './SportCenterBits';

/**
 * Sport in de Live TV-speler: speelt deze zender een wedstrijd, dan de stand in de infobalk en een
 * wedstrijdcentrum aan de zijkant (stats, verloop, opstelling, stand), zoals een sport-tv-kanaal dat doet.
 */

export type SportSkin = 'off' | 'score' | 'full' | 'custom';
export interface SkinParts { score: boolean; strip: boolean; ticker: boolean; panel: boolean }
export interface TickerItem { key: string; text: string; score?: string; fav?: boolean }
export interface TickerBlock { kind: string; label: string; items: TickerItem[] }
const PRESET: Record<SportSkin, SkinParts> = {
  off: { score: false, strip: false, ticker: false, panel: false },
  score: { score: true, strip: false, ticker: false, panel: false },
  full: { score: true, strip: true, ticker: true, panel: true },
  custom: { score: true, strip: true, ticker: true, panel: true },
};

export function useChannelMatch(accountId: string, streamId: number, catchup: boolean) {
  const [match, setMatch] = useState<{ path: string; eventId: string } | null>(null);
  const [detail, setDetail] = useState<MatchDetail | null>(null);
  const [skin, setSkinState] = useState<SportSkin>('full');
  const [parts, setParts] = useState<SkinParts>(PRESET.full);
  const [ticker, setTicker] = useState<TickerBlock[]>([]);
  const [custom, setCustom] = useState<SkinParts | null>(null);
  const scores = useRef(new Map<string, string>());
  const flash = useRef<{ item: TickerItem; until: number }[]>([]);
  useEffect(() => {
    if (catchup) return;
    let alive = true;
    const find = () => hubApi.onChannel(accountId, streamId).then((r) => {
      if (!alive) return;
      setMatch((m) => (m && r.match && m.path === r.match.path && m.eventId === r.match.eventId ? m : r.match));
      if (r.skin) setSkinState(r.skin);
      if (r.parts) setParts(r.parts);
      setCustom(r.custom ?? null);
      // Doelpunt elders: vooraan in de lichtkrant, een minuut lang
      const now = Date.now();
      for (const b of r.ticker ?? []) for (const it of b.items) {
        if (!it.score) continue;
        const before = scores.current.get(it.key);
        scores.current.set(it.key, it.score);
        if (before && before !== it.score && !r.hideScores) flash.current.push({ item: { ...it, key: `goal-${it.key}-${it.score}` }, until: now + 90_000 });
      }
      flash.current = flash.current.filter((f) => f.until > now);
      const goals = flash.current.map((f) => f.item);
      setTicker([...(goals.length ? [{ kind: 'goal', label: 'DOELPUNT', items: goals }] : []), ...(r.ticker ?? [])]);
    }).catch(() => undefined);
    void find();
    // Vaker zolang er een wedstrijd is (lichtkrant met standen elders)
    const t = window.setInterval(find, 60_000);
    return () => { alive = false; window.clearInterval(t); setMatch(null); setDetail(null); setTicker([]); };
  }, [accountId, streamId, catchup]);
  useEffect(() => {
    if (!match) { setDetail(null); return; }
    let alive = true;
    const load = () => scApi.match(match.path, match.eventId).then((d) => alive && setDetail(d)).catch(() => undefined);
    void load();
    const t = window.setInterval(load, 20_000);
    return () => { alive = false; window.clearInterval(t); };
  }, [match]);
  /** Snelle keuze in de speler (onthouden, geldt ook voor de apps) */
  const setSkin = (v: SportSkin) => { setSkinState(v); setParts(v === 'custom' ? custom ?? PRESET.full : PRESET[v]); void hubApi.saveFollow({ skin: v }).catch(() => undefined); };
  const any = parts.score || parts.strip || parts.ticker || parts.panel;
  /** Volgende snelle keuze: uit → stand → volledig → aangepast (als je die hebt) → uit */
  const next = (): SportSkin => (skin === 'off' ? 'score' : skin === 'score' ? 'full' : skin === 'full' && custom ? 'custom' : 'off');
  return { detail: any ? detail : null, found: detail, skin, parts, setSkin, ticker, next };
}

export const SKIN_LABEL: Record<SportSkin, string> = { off: 'Sport: uit', score: 'Sport: stand', full: 'Sport: volledig', custom: 'Sport: aangepast' };

/** Minuut uit "63'" of "45'+2'" voor de tijdlijn. */
function minuteOf(t: string | null | undefined): number | null {
  const m = (t ?? '').match(/(\d{1,3})(?:'?\s*\+\s*(\d{1,2}))?/);
  return m ? Number(m[1]) + (m[2] ? Number(m[2]) / 10 : 0) : null;
}

/** Vaste stand linksboven (ook als de bediening weg is), zoals een tv-zender dat doet. */
export function FixedScore({ d }: { d: MatchDetail }) {
  return <div className="sp-fixed"><ScoreChip d={d} /></div>;
}

/** Balk onderin: stand, verloop op een tijdlijn en wat er straks komt. */
export function SportStrip({ d }: { d: MatchDetail }) {
  const hide = useHideScores();
  const m = d.match;
  const now = minuteOf(m.minute) ?? (m.state === 'post' ? 90 : 0);
  const events = d.events.filter((e) => ['goal', 'pen', 'own', 'red'].includes(e.kind));
  const len = Math.max(90, now, ...events.map((e) => minuteOf(e.minute) ?? 0)) > 95 ? 120 : 90;
  const pos = (n: number) => `${Math.min(100, (n / len) * 100)}%`;
  return (
    <div className="sp-strip" aria-label="Wedstrijd">
      <div className="sp-strip-main">
        <div className="sp-strip-score">
          <span className="sp-strip-league">{m.leagueName}</span>
          <b className={hide ? 'is-hidden' : ''} data-score>{m.state === 'pre' ? 'vs' : `${m.home.score ?? 0} – ${m.away.score ?? 0}`}</b>
          {m.state === 'in' && <em>{m.minute ?? m.detail}</em>}
          {m.state === 'post' && <em className="is-done">Afgelopen</em>}
        </div>
        <div className="sp-strip-line">
          <span className="sp-strip-head"><span>Verloop</span>{m.state === 'in' && <span className="sp-strip-live">LIVE · {m.minute ?? m.detail}</span>}</span>
          <div className="sp-tl">
            <i className="sp-tl-bar" />
            <i className="sp-tl-done" style={{ width: pos(now) }} />
            <i className="sp-tl-half" style={{ left: pos(45) }}><span>rust</span></i>
            {m.state === 'in' && <i className="sp-tl-now" style={{ left: pos(now) }} />}
            {!hide && events.map((e, i) => {
              const at = minuteOf(e.minute);
              if (at === null) return null;
              return <span key={i} className={`sp-tl-ev is-${e.side === 'away' ? 'away' : 'home'}${e.kind === 'red' ? ' is-red' : ''}`} style={{ left: pos(at) }}>{e.minute} {(e.player ?? e.text).split(' ').slice(-1)[0]}</span>;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Kleine stand voor de infobalk ("AJA 2-1 INT 63'"). */
export function ScoreChip({ d }: { d: MatchDetail }) {
  const hide = useHideScores();
  const m = d.match;
  return (
    <span className="sp-chip" aria-label={`${m.home.short} tegen ${m.away.short}${hide ? '' : `, ${m.home.score ?? 0}-${m.away.score ?? 0}`}`}>
      <b style={{ background: m.home.color ? `#${m.home.color}` : undefined }}>{m.home.abbr || m.home.short.slice(0, 3)}</b>
      <i>{m.state === 'pre' ? 'vs' : hide ? '•–•' : `${m.home.score ?? 0}-${m.away.score ?? 0}`}</i>
      <b style={{ background: m.away.color ? `#${m.away.color}` : undefined }}>{m.away.abbr || m.away.short.slice(0, 3)}</b>
      {m.state === 'in' && <em>{m.minute ?? m.detail}</em>}
    </span>
  );
}

type Tab = 'stats' | 'verloop' | 'opstelling' | 'stand';

export function SportPanel({ d, onClose }: { d: MatchDetail; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('stats');
  const [table, setTable] = useState<SGroup[] | null>(null);
  const hide = useHideScores();
  const m = d.match;
  useEffect(() => {
    if (tab !== 'stand' || table) return;
    scApi.standings({ path: m.path }).then((r) => setTable(r.groups)).catch(() => setTable([]));
  }, [tab, table, m.path]);
  const tabs: [Tab, string][] = [['stats', 'Stats'], ['verloop', 'Verloop'], ['opstelling', 'Opstelling'], ['stand', 'Stand']];
  return (
    <aside className="sp-panel" aria-label="Wedstrijdcentrum" onClick={(e) => e.stopPropagation()}>
      <div className="sp-head">
        <span className="sp-title">Wedstrijd<span>centrum</span></span>
        <button className="icon-mini" onClick={onClose} aria-label="Sluiten">✕</button>
      </div>
      <div className="sp-score">
        <span><Crest logo={m.home.logo} name={m.home.short} size={40} />{m.home.short}</span>
        <b className={hide ? 'is-hidden' : ''} data-score>{m.state === 'pre' ? 'vs' : `${m.home.score ?? 0} – ${m.away.score ?? 0}`}</b>
        <span>{m.away.short}<Crest logo={m.away.logo} name={m.away.short} size={40} /></span>
      </div>
      <nav className="sp-tabs" data-focus-group>
        {tabs.map(([id, label]) => <button key={id} type="button" aria-pressed={tab === id} onClick={() => setTab(id)}>{label}</button>)}
      </nav>
      <div className="sp-body">
        {tab === 'stats' && (d.stats.length ? d.stats.map((s) => {
          const t = (s.homeN + s.awayN) || 1;
          return (
            <div key={s.label} className="sp-stat">
              <span className="sp-stat-row"><b>{s.home}</b><span>{s.label}</span><b>{s.away}</b></span>
              <span className="sp-bar"><i style={{ width: `${(s.homeN / t) * 100}%` }} /><i className="is-away" /></span>
            </div>
          );
        }) : <p className="subtle">Nog geen statistieken.</p>)}
        {tab === 'verloop' && (hide ? <p className="subtle">Uitslagen zijn verborgen.</p> : d.events.length ? (
          <ul className="sp-events">{d.events.filter((e) => e.kind !== 'other').map((e, i) => <li key={i} className={`sp-ev-${e.side ?? 'mid'} sp-k-${e.kind}`}><b>{e.minute}</b> {e.player ?? e.text}{e.extra ? <span className="subtle"> · {e.extra}</span> : null}</li>)}</ul>
        ) : <p className="subtle">Nog niets gebeurd.</p>)}
        {tab === 'opstelling' && (d.lineups.length ? d.lineups.map((l) => (
          <div key={l.side} className="sp-lineup">
            <b>{l.side === 'home' ? m.home.short : m.away.short}{l.formation ? ` · ${l.formation}` : ''}</b>
            <ol>{l.starters.map((p) => <li key={`${p.n}${p.name}`}><i>{p.n}</i> {p.name}{p.subOut ? ' ↓' : ''}</li>)}</ol>
            {l.bench.length > 0 && <span className="subtle">Wissels: {l.bench.map((p) => `${p.name}${p.subIn ? ' ↑' : ''}`).join(', ')}</span>}
          </div>
        )) : <p className="subtle">Opstellingen zijn er nog niet.</p>)}
        {tab === 'stand' && (!table ? <p className="subtle">Laden…</p> : table.length ? table.slice(0, 1).map((g) => (
          <table key={g.name} className="sp-table">
            <thead><tr><th>#</th><th>Club</th><th>G</th><th>DS</th><th>P</th></tr></thead>
            <tbody>{g.rows.map((r) => <tr key={r.team.id} className={r.team.id === m.home.id || r.team.id === m.away.id ? 'is-here' : ''}><td>{r.rank}</td><td>{r.team.short}</td><td>{r.played}</td><td>{r.gd}</td><td><b>{r.points}</b></td></tr>)}</tbody>
          </table>
        )) : <p className="subtle">Geen stand voor deze competitie.</p>)}
      </div>
    </aside>
  );
}

/** Lichtkrant onderin: blokken die je zelf kiest (live elders, jouw clubs, uitslagen, stand, straks). */
export function SportTicker({ blocks }: { blocks: TickerBlock[] }) {
  if (!blocks.length) return null;
  const run = blocks.map((b) => (
    <span key={b.kind} className={`sp-tk-block sp-tk-${b.kind}`}>
      <b>{b.label}</b>
      {b.items.map((i) => <span key={i.key} className={i.fav ? 'is-fav' : ''}>{i.text}</span>)}
    </span>
  ));
  return (
    <div className="sp-ticker" aria-label="Lichtkrant">
      <div className="sp-ticker-run"><span>{[0, 1].map((n) => <Fragment key={n}>{run}</Fragment>)}</span></div>
    </div>
  );
}
